// frontend/src/components/flags/FlagTree.tsx
/**
 * Renders a manifest. Knows nothing about Atlas, tabs, sub-tabs or sections.
 *
 * Node nesting comes from `children`, and the segment between a parent and a
 * child is already inside the child's `path`. That is the whole point: when GUS
 * adds a container kind, this renders it without a portal release.
 *
 * It also never prunes unknown keys (controller ruling R2): a key present in a
 * tenant's stored JSON but absent from the manifest is rendered read-only as
 * "unknown to this build" rather than silently dropped on save.
 */
import React, { useId, useState } from 'react';
import styled, { css } from 'styled-components';
import { Collapse } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  spacing, typography, borderRadius, brandColors,
} from '../../design-system/tokens';
import { media } from '../../design-system/responsive';
import { truncate } from '../../design-system/mixins';
import { FlagManifest, FlagNode } from '../../services/api';
import FlagLeafControl from './FlagLeafControl';

/**
 * What a caller may add to one node without this tree learning what the node
 * is: header badges, notices above its controls, a body of its own below them,
 * and keys it edits there (so they are not reported as unknown). The editor
 * uses it for City Satisfaction; the tree stays generic.
 */
export interface NodeDecoration {
  badges?: Array<{ tone: Tone; text: string; textAr?: string }>;
  notices?: React.ReactNode;
  body?: React.ReactNode;
  declaredKeys?: string[];
}

export type Decorate = (node: FlagNode) => NodeDecoration | undefined;

interface Props {
  manifest: FlagManifest;
  storedFlags: Record<string, any>;
  onChange: (path: string, value: any) => void;
  decorate?: Decorate;
}

/**
 * Read a dot path out of the tenant's whole stored `feature_flags` object.
 *
 * Resolved from the ROOT, not from `feature_flags.modules`: that is what GUS
 * does (`getValueByPath(config.features, path)`, where `config.features` IS
 * `feature_flags`), and nine of the real manifest's eighty nodes --
 * `utilities.settings` and its eight tab children -- sit outside the `modules`
 * family. Stripping a leading `modules.` here read them from
 * `feature_flags.modules.utilities.settings.*`, a place nothing writes.
 */
export function readStored(storedFlags: Record<string, any>, path: string): any {
  const parts = path.split('.');
  let node: any = storedFlags;
  for (let i = 0; i < parts.length; i += 1) {
    const part = parts[i];
    // `tab: true` is the shorthand for `tab: {enabled: true}`: its `enabled` is the boolean itself.
    if (typeof node === 'boolean' && part === 'enabled' && i === parts.length - 1) return node;
    if (node === null || typeof node !== 'object' || !(part in node)) return undefined;
    node = node[part];
  }
  return node;
}

/** Which alias path, if any, this tenant has set — "first key present decides". */
export function decidingPath(node: FlagNode, storedFlags: Record<string, any>): string {
  const candidates = [node.path, ...(node.aliases || []).map((a) => a.path)];
  // Node presence, NOT `.enabled` presence. The contract says "the first path
  // *present* decides"; a tenant that stored only `order` or a caption under an
  // alias would otherwise fall through to the canonical path and read and write
  // its leaves to the wrong place.
  return candidates.find((p) => readStored(storedFlags, p) !== undefined) ?? node.path;
}

/** The server passes the manifest through untouched, and its validation is a floor. */
const withPath = (nodes?: FlagNode[]) =>
  (nodes || []).filter((n) => n && typeof n.path === 'string');

/**
 * The segment of `childPath` immediately after `nodePath` — the container name
 * ("tabs", "subtabs", "sections", ...) without hard-coding any of them. Returns
 * null if `childPath` is not actually nested under `nodePath`.
 */
function containerSegmentOf(nodePath: string, childPath: string): string | null {
  const prefix = `${nodePath}.`;
  if (!childPath.startsWith(prefix)) return null;
  const rest = childPath.slice(prefix.length);
  const dot = rest.indexOf('.');
  return dot === -1 ? rest : rest.slice(0, dot);
}

/**
 * Stored keys under `storedPath` that the manifest node does not declare.
 * Returns dot paths, e.g. ['modules.atlas.tabs.retired_tab'].
 *
 * A node's declared keys are its `leaves[].key` values plus the container
 * segment of each child (derived from paths, never hard-coded) -- or, for a
 * child stored directly under the node with no container between, that
 * child's own key. Storage is read
 * from the node's deciding path, so an alias-stored node is judged against the
 * keys actually in use. Inside a container the node does declare, a stored
 * entry matching no child of that container is unknown too, reported at its
 * own full dot path.
 */
export function unknownKeysUnder(
  node: FlagNode,
  storedFlags: Record<string, any>,
  declaredKeys: string[] = [],
): string[] {
  const deciding = decidingPath(node, storedFlags);
  const stored = readStored(storedFlags, deciding);
  if (stored === null || typeof stored !== 'object' || Array.isArray(stored)) return [];

  const leafKeys = new Set([...(node.leaves || []).map((l) => l.key), ...declaredKeys]);
  const childKeysByContainer = new Map<string, Set<string>>();
  for (const child of withPath(node.children)) {
    // A child stored directly under this node, with no container segment
    // between (a tab's `config`, a config's `topic_icons`), is a declared key
    // like a leaf. Treating its own key as a container would report every
    // entry inside it as unknown; the child's own check covers those.
    if (child.path === `${node.path}.${child.key}`) {
      leafKeys.add(child.key);
      continue;
    }
    const container = containerSegmentOf(node.path, child.path);
    if (container === null) continue;
    if (!childKeysByContainer.has(container)) childKeysByContainer.set(container, new Set());
    childKeysByContainer.get(container)!.add(child.key);
  }

  const unknown: string[] = [];
  for (const key of Object.keys(stored)) {
    if (leafKeys.has(key)) continue;

    if (childKeysByContainer.has(key)) {
      const knownChildKeys = childKeysByContainer.get(key)!;
      const containerValue = stored[key];
      if (containerValue !== null && typeof containerValue === 'object' && !Array.isArray(containerValue)) {
        for (const childKey of Object.keys(containerValue)) {
          if (!knownChildKeys.has(childKey)) unknown.push(`${deciding}.${key}.${childKey}`);
        }
      }
      continue;
    }

    unknown.push(`${deciding}.${key}`);
  }
  return unknown;
}

/**
 * Stored modules no manifest module (or alias) declares, as real dot paths.
 *
 * `storedFlags` is the whole `feature_flags` object, so a manifest module's
 * path has two segments — a family (`modules`, `utilities`) and a key. A stored
 * family the manifest does declare is therefore compared entry by entry
 * (`modules.old_module`); a stored top-level key belonging to no declared
 * family at all is reported whole (`experimental`).
 *
 * Reporting the real path is the point: an earlier version indexed storage by
 * the last segment alone and had to decide whether to fabricate a "modules."
 * prefix for the label. With paths resolved from the root there is nothing to
 * fabricate — the family is right there in the manifest.
 */
export function unknownModules(manifest: FlagManifest, storedFlags: Record<string, any>): string[] {
  if (storedFlags === null || typeof storedFlags !== 'object') return [];

  // family ("modules") -> the keys declared under it ("atlas", "fieldnotes", ...)
  const declared = new Map<string, Set<string>>();
  for (const module of withPath(manifest.modules)) {
    const paths = [module.path, ...(module.aliases || []).map((a) => a.path)];
    for (const path of paths) {
      const [family, ...rest] = path.split('.');
      if (!declared.has(family)) declared.set(family, new Set());
      if (rest.length) declared.get(family)!.add(rest.join('.'));
    }
  }

  const unknown: string[] = [];
  for (const family of Object.keys(storedFlags)) {
    const knownKeys = declared.get(family);
    if (!knownKeys) {
      unknown.push(family);
      continue;
    }
    const familyValue = storedFlags[family];
    if (familyValue === null || typeof familyValue !== 'object' || Array.isArray(familyValue)) continue;
    for (const key of Object.keys(familyValue)) {
      if (!knownKeys.has(key)) unknown.push(`${family}.${key}`);
    }
  }
  return unknown;
}


// =============================================================================
// STYLED COMPONENTS
//
// The catalogue above this tree draws three levels with three cues: a module is
// a bordered box with a tinted band, a tab is a plain row on a recessed panel
// separated by solid rules, a sub-item is a quieter row on a deeper panel
// separated by dashed ones. The manifest tree is recursive and the catalogue is
// not, so depth is clamped to those same three treatments -- level 2 is "this
// and anything under it". Nothing new is invented to express nesting.
// =============================================================================

type Level = 0 | 1 | 2;
export type Tone = 'neutral' | 'info' | 'warning' | 'danger';

const levelOf = (depth: number): Level => (depth <= 0 ? 0 : depth === 1 ? 1 : 2);

const toneColor = (tone: Tone) => (props: { theme?: any }) => {
  switch (tone) {
    case 'info':
      return props.theme?.colors?.accent?.info || brandColors.blue;
    case 'warning':
      return props.theme?.colors?.accent?.warning || brandColors.warning;
    case 'danger':
      return props.theme?.colors?.accent?.danger || brandColors.danger;
    default:
      // text.secondary, not tertiary: at 11px on a tinted panel tertiary lands
      // near 3:1, which is a ring contrast, not a reading one.
      return props.theme?.colors?.text?.secondary || '#94a3b8';
  }
};

/**
 * One ring everywhere, matching the leaf controls: 2px of brand teal clears
 * 3:1 on every surface here in both themes, and no rule below transitions
 * `outline`, so it appears the instant the key lands.
 */
const focusRing = css`
  &:focus-visible {
    outline: 2px solid ${props => props.theme?.colors?.accent?.primary || brandColors.silaTeal};
    outline-offset: -2px;
  }
`;

const TreeRoot = styled.div`
  display: flex;
  flex-direction: column;

  /* MUI's Collapse animates height inline; !important outranks that. */
  @media (prefers-reduced-motion: reduce) {
    &,
    & *,
    & *::before,
    & *::after {
      transition-duration: 0.01ms !important;
      animation-duration: 0.01ms !important;
    }
  }
`;

const NodeShell = styled.div<{ $level: Level }>`
  min-width: 0;

  ${props => props.$level === 0 && css`
    border: 1px solid ${props.theme?.colors?.border?.primary || 'rgba(255, 255, 255, 0.1)'};
    border-radius: ${borderRadius.md};
    margin-bottom: ${spacing['2']};
    overflow: hidden;

    &:last-child {
      margin-bottom: 0;
    }
  `}

  ${props => props.$level === 1 && css`
    &:not(:first-child) {
      border-top: 1px solid ${props.theme?.colors?.border?.secondary || 'rgba(255, 255, 255, 0.05)'};
    }
  `}

  ${props => props.$level === 2 && css`
    &:not(:first-child) {
      border-top: 1px dashed ${props.theme?.colors?.border?.secondary || 'rgba(255, 255, 255, 0.05)'};
    }
  `}
`;

const NodeHeader = styled.button<{ $level: Level; $enabled: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing['2']};
  width: 100%;
  text-align: left;
  font-family: inherit;
  border: none;
  cursor: pointer;

  padding: ${props => (props.$level === 0
    ? spacing['3']
    : props.$level === 1
      ? `${spacing['2']} ${spacing['3']}`
      : `${spacing['1']} ${spacing['3']}`)};

  /* The catalogue tints a module's band when the module is on. Same here. */
  background: ${props => (props.$level === 0 && props.$enabled
    ? `${props.theme?.colors?.accent?.primary || brandColors.silaTeal}10`
    : 'transparent')};

  &:hover {
    background: ${props => props.theme?.colors?.background?.hover || 'rgba(255, 255, 255, 0.05)'};
  }

  &:active {
    background: ${props => `${props.theme?.colors?.accent?.primary || brandColors.silaTeal}20`};
  }

  ${focusRing}
`;

const NodeHeaderMain = styled.span`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing['1']} ${spacing['2']};
  flex: 1;
  min-width: 0;
`;

const NodeLabel = styled.span<{ $level: Level }>`
  ${truncate}
  /* Shrink and ellipsise rather than push the row wider than the phone. */
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;

  font-size: ${props => (props.$level === 0
    ? typography.fontSize.base
    : props.$level === 1
      ? typography.fontSize.sm
      : typography.fontSize.xs)};

  font-weight: ${props => (props.$level === 0
    ? typography.fontWeight.semibold
    : typography.fontWeight.medium)};

  color: ${props => (props.$level === 2
    ? props.theme?.colors?.text?.secondary || '#94a3b8'
    : props.theme?.colors?.text?.primary || '#ffffff')};
`;

/** The catalogue's ArabicLabel voice: quieter than the label, set right to left. */
const ArabicLabel = styled.span`
  ${truncate}
  flex: 0 1 auto;
  min-width: 0;
  font-size: ${typography.fontSize.xs};
  color: ${props => props.theme?.colors?.text?.tertiary || '#64748b'};
`;

const Chevron = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  color: ${props => props.theme?.colors?.text?.secondary || '#94a3b8'};
`;

/** The catalogue's RoleBadge voice: tinted fill, no border, small and quiet. */
const Badge = styled.span<{ $tone: Tone }>`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  padding: 2px ${spacing['1.5']};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  line-height: ${typography.lineHeight.tight};
  white-space: nowrap;
  color: ${props => toneColor(props.$tone)(props)};
  background: ${props => `${toneColor(props.$tone)(props)}20`};
  border-radius: ${borderRadius.sm};
`;

/** A stored dot path is code, so it is set as code — and it may break anywhere. */
const PathBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 2px ${spacing['1.5']};
  font-family: ${typography.fontFamily.mono};
  font-size: ${typography.fontSize.xs};
  color: ${props => props.theme?.colors?.text?.secondary || '#94a3b8'};
  background: ${props => props.theme?.colors?.background?.tertiary || 'rgba(20, 20, 20, 0.5)'};
  border: 1px solid ${props => props.theme?.colors?.border?.secondary || 'rgba(255, 255, 255, 0.05)'};
  border-radius: ${borderRadius.sm};
  word-break: break-all;
`;

/**
 * The inline notice. A rule down the accent side, a tint of the same colour,
 * caption-weight text — the same device Card already uses for `accentBorder`,
 * turned on its side. No icon: at this size an icon is noise, and the tone is
 * already carried by the rule.
 */
const Notice = styled.div<{ $tone: Tone }>`
  display: block;
  padding: ${spacing['2']} ${spacing['3']};
  margin-bottom: ${spacing['2']};
  font-size: ${typography.fontSize.xs};
  line-height: ${typography.lineHeight.normal};
  color: ${props => props.theme?.colors?.text?.secondary || '#94a3b8'};
  background: ${props => `${toneColor(props.$tone)(props)}14`};
  border-left: 2px solid ${props => toneColor(props.$tone)(props)};
  border-radius: 0 ${borderRadius.sm} ${borderRadius.sm} 0;
`;

const NoticeTitle = styled.span`
  display: block;
  margin-bottom: ${spacing['1']};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  color: ${props => props.theme?.colors?.text?.secondary || '#94a3b8'};
`;

const PathList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing['1']};
  min-width: 0;
`;

const NodeBody = styled.div<{ $level: Level }>`
  padding: ${spacing['3']};

  ${media.belowXs} {
    padding: ${spacing['2']};
  }

  background: ${props => (props.$level === 0
    ? props.theme?.colors?.background?.secondary || 'rgba(26, 26, 26, 0.5)'
    : props.$level === 1
      ? props.theme?.colors?.background?.tertiary || 'rgba(20, 20, 20, 0.5)'
      : 'transparent')};

  ${props => props.$level === 0 && css`
    border-top: 1px solid ${props.theme?.colors?.border?.primary || 'rgba(255, 255, 255, 0.1)'};
  `}
`;

const LeafRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: ${spacing['2']} ${spacing['4']};
  min-width: 0;
`;

/** Breathing room between a node's own controls and what follows them. */
const AfterLeaves = styled.div`
  margin-top: ${spacing['3']};
`;

const ChildList = styled.div`
  margin-top: ${spacing['3']};
  padding-left: ${spacing['4']};
  border-left: 1px solid ${props => props.theme?.colors?.border?.secondary || 'rgba(255, 255, 255, 0.05)'};
  min-width: 0;

  ${media.belowXs} {
    padding-left: ${spacing['2']};
  }
`;

// =============================================================================
// RENDER
// =============================================================================

const UnknownKeysNotice: React.FC<{ paths: string[] }> = ({ paths }) => (
  <Notice $tone="neutral">
    <NoticeTitle>Stored for this tenant but unknown to this build. Kept, not deleted.</NoticeTitle>
    <PathList>
      {paths.map((path) => (
        <PathBadge key={path}>{path}</PathBadge>
      ))}
    </PathList>
  </Notice>
);

const NodeRow: React.FC<{ node: FlagNode; storedFlags: Record<string, any>;
  rolesVocabulary: string[]; onChange: Props['onChange']; depth: number; decorate?: Decorate }> = ({
  node, storedFlags, rolesVocabulary, onChange, depth, decorate,
}) => {
  // Same disclosure as the catalogue: a module opens, everything under it
  // starts closed, and each level opens on its own.
  const [expanded, setExpanded] = useState(depth === 0);
  const bodyId = useId();
  const level = levelOf(depth);

  const deciding = decidingPath(node, storedFlags);
  const aliasInUse = deciding !== node.path;
  // Node presence, the same test decidingPath uses: a canonical node holding only
  // `order` still shadows an alias's `enabled: false`, and that must be flagged.
  const bothWritten =
    readStored(storedFlags, node.path) !== undefined &&
    (node.aliases || []).some((a) => readStored(storedFlags, a.path) !== undefined);

  const subtabs = withPath(node.children);
  // Each child is judged at ITS deciding path, not its canonical one. A child
  // whose value is stored under a deprecated alias reads `undefined` at
  // `${c.path}.enabled`, which is not `false`, so one alias-stored child used to
  // suppress the sub-tab-floor warning for the whole surface.
  const allChildrenOff =
    subtabs.length > 0 &&
    subtabs.every(
      (c) => readStored(storedFlags, `${decidingPath(c, storedFlags)}.enabled`) === false
    );

  const decoration = decorate?.(node);
  const unknownHere = unknownKeysUnder(node, storedFlags, decoration?.declaredKeys);
  const leaves = node.leaves || [];
  // Presentation only -- GUS's own rule (absent, null and true all mean on),
  // used to tint a module band the way the catalogue tints an enabled module.
  const selfEnabled = readStored(storedFlags, `${deciding}.enabled`) !== false;

  return (
    <NodeShell $level={level}>
      <NodeHeader
        type="button"
        $level={level}
        $enabled={selfEnabled}
        aria-expanded={expanded}
        aria-controls={bodyId}
        onClick={() => setExpanded((open) => !open)}
      >
        <NodeHeaderMain>
          <NodeLabel $level={level}>{node.label}</NodeLabel>
          {node.label_ar && <ArabicLabel lang="ar" dir="rtl">{node.label_ar}</ArabicLabel>}
          <Badge $tone="neutral">{node.kind}</Badge>
          {decoration?.badges?.map((b) => (
            <Badge key={b.text} $tone={b.tone}>
              {b.text}
              {b.textAr && <span lang="ar" dir="rtl">&nbsp;/ {b.textAr}</span>}
            </Badge>
          ))}
          {node.gated_by && <Badge $tone="info">gated elsewhere</Badge>}
          {aliasInUse && <Badge $tone="warning">set via deprecated key</Badge>}
        </NodeHeaderMain>
        <Chevron aria-hidden="true">
          {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </Chevron>
      </NodeHeader>

      <Collapse in={expanded}>
        <NodeBody id={bodyId} $level={level}>
          {node.gated_by && (
            <Notice $tone="info">
              {node.gated_note || `Controlled by ${node.gated_by}, not by this entry.`}
            </Notice>
          )}
          {bothWritten && (
            <Notice $tone="warning">
              This tenant has both <PathBadge as="code">{node.path}</PathBadge> and a deprecated
              alias set. Only the first one present is read — never write both. Remove the
              deprecated key.
            </Notice>
          )}
          {aliasInUse && !bothWritten && (
            <Notice $tone="warning">
              This value is stored under the deprecated path{' '}
              <PathBadge as="code">{deciding}</PathBadge>. Migrate it to{' '}
              <PathBadge as="code">{node.path}</PathBadge>.
            </Notice>
          )}
          {decoration?.notices}
          {allChildrenOff && (
            <Notice $tone="warning">
              Every item under this surface is disabled. GUS keeps the first one visible rather than
              rendering an empty body, so the last toggle will be overridden.
            </Notice>
          )}

          {leaves.length > 0 && (
            <LeafRow>
              {leaves.map((leaf) => (
                <FlagLeafControl
                  key={leaf.key}
                  leaf={leaf}
                  node={node}
                  rolesVocabulary={rolesVocabulary}
                  value={readStored(storedFlags, `${deciding}.${leaf.key}`)}
                  // GUS reads gated_by's path first, so a write through this node's
                  // own control would be silently ignored. The notice above already
                  // says why; disable rather than reroute -- gated_by's own node has
                  // its own control, and two controls writing one path is worse.
                  disabled={!!node.gated_by}
                  onChange={(v) => onChange(`${deciding}.${leaf.key}`, v)}
                />
              ))}
            </LeafRow>
          )}

          {decoration?.body && <AfterLeaves>{decoration.body}</AfterLeaves>}

          {unknownHere.length > 0 && (
            <AfterLeaves>
              <UnknownKeysNotice paths={unknownHere} />
            </AfterLeaves>
          )}

          {subtabs.length > 0 && (
            <ChildList>
              {subtabs.map((child) => (
                <NodeRow
                  key={child.path}
                  node={child}
                  storedFlags={storedFlags}
                  rolesVocabulary={rolesVocabulary}
                  onChange={onChange}
                  depth={depth + 1}
                  decorate={decorate}
                />
              ))}
            </ChildList>
          )}
        </NodeBody>
      </Collapse>
    </NodeShell>
  );
};

const FlagTree: React.FC<Props> = ({ manifest, storedFlags, onChange, decorate }) => {
  const unknownMods = unknownModules(manifest, storedFlags);
  // The backend's validation is a floor (parse_manifest checks only version
  // and a non-empty modules array), so a manifest can arrive with no
  // roles_vocabulary at all. Default it here rather than crash the whole
  // picker on rolesVocabulary.map inside every rbac leaf.
  const rolesVocabulary = Array.isArray(manifest.roles_vocabulary) ? manifest.roles_vocabulary : [];

  return (
    <TreeRoot>
      {unknownMods.length > 0 && <UnknownKeysNotice paths={unknownMods} />}
      {withPath(manifest.modules).map((node) => (
        <NodeRow
          key={node.path}
          node={node}
          storedFlags={storedFlags}
          rolesVocabulary={rolesVocabulary}
          onChange={onChange}
          depth={0}
          decorate={decorate}
        />
      ))}
    </TreeRoot>
  );
};

/** The tree's notice and path styles, for decorations that sit inside its nodes. */
export { Notice, NoticeTitle, PathBadge };

export default FlagTree;
